<template>
  <div class="flex justify-end gap-2">
    <CButton
      v-if="!hideCopy"
      v-tooltip="t('canvasUI.common.edit')"
      circle
      :loading="isEditing"
      @click="handleActions('edit')"
    >
      <template #icon>
        <CIcon icon="edit" size="16px" />
      </template>
    </CButton>

    <CButton
      v-if="!hideDelete"
      v-tooltip="t('canvasUI.components.themes.CThemeManagement.actions.buttons.tooltips.delete')"
      circle
      :loading="isDeleting"
      secondary
      type="error"
      @click="handleActions('delete')"
    >
      <template #icon>
        <CIcon icon="trash" size="16px" />
      </template>
    </CButton>

    <CDropdown :options="getActionsMenu()" trigger="click" @select="handleActions">
      <CButton v-tooltip="t('canvasUI.components.themes.CThemeManagement.actions.buttons.tooltips.actions')" circle>
        <template #icon>
          <CIcon icon="ellipsis" size="16px" />
        </template>
      </CButton>
    </CDropdown>
  </div>
</template>

<script setup lang="ts">
import { h } from 'vue'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CThemeManagementActionsCellIconWrapper from './CThemeManagementActionsCellIconWrapper.vue'

import tooltipDirective from '@lib/directives/tooltip'
import useThemeVars from '@lib/composables/useThemeVars'
import type { DropdownMixedOption } from 'naive-ui/es/dropdown/src/interface'

const props = defineProps<{
  hideCopy?: boolean
  hideEdit?: boolean
  hideDelete?: boolean
  hideViewNewTab?: boolean
  hideToggleDefault?: boolean
  isEditing?: boolean
  isDeleting?: boolean
  isDefault?: boolean
}>()

const emit = defineEmits<{
  copy: []
  edit: []
  delete: []
  viewNewTab: []
  toggleDefault: []
}>()

const { t } = useI18n()
const themeVars = useThemeVars()
const vTooltip = tooltipDirective

const FONT_SIZE_CLASS = 'font-size-4'

function getActionsMenu() {
  const actionsMenu: DropdownMixedOption[] = []

  if (!props.hideEdit) {
    actionsMenu.push({
      label: () => h('div', { class: FONT_SIZE_CLASS }, t('canvasUI.components.themes.CThemeManagement.actions.edit')),
      key: 'edit',
      icon: () => h(CIcon, { icon: 'edit' }),
    })
  }

  if (!props.hideViewNewTab) {
    actionsMenu.push({
      label: () =>
        h('div', { class: FONT_SIZE_CLASS }, t('canvasUI.components.themes.CThemeManagement.actions.viewNewTab')),
      key: 'viewNewTab',
      icon: () => h(CIcon, { icon: 'browsers' }),
    })
  }

  if (!props.hideCopy || !props.hideDelete || !props.hideToggleDefault) {
    actionsMenu.push({ key: 'd1', type: 'divider' })
  }
  if (!props.hideToggleDefault && !props.isDefault) {
    actionsMenu.push({
      label: () =>
        h('div', { class: 'flex justify-between items-center gap-4' }, [
          h(
            'div',
            { class: FONT_SIZE_CLASS, style: { color: props.isDefault ? themeVars.value.error : undefined } },
            t('canvasUI.components.themes.CThemeManagement.actions.setDefaultTheme'),
          ),
          h(CThemeManagementActionsCellIconWrapper, {
            icon: 'question',
            faType: 'fas',
            color: themeVars.value.primary6,
            iconColor: themeVars.value.primary,
            tooltip: t('canvasUI.components.themes.CThemeManagement.actions.buttons.tooltips.defaultThemeInfo'),
          }),
        ]),
      key: 'toggleDefault',
      icon: () =>
        h(CIcon, {
          faType: 'fa-kit',
          icon: 'regular-palette-circle-check',
          color: props.isDefault ? themeVars.value.error : undefined,
        }),
    })
  }

  if (!props.hideCopy) {
    actionsMenu.push({
      label: () =>
        h('div', { class: FONT_SIZE_CLASS }, t('canvasUI.components.themes.CThemeManagement.actions.duplicate')),
      key: 'copy',
      icon: () => h(CIcon, { icon: 'copy' }),
    })
  }

  if (!props.hideDelete) {
    actionsMenu.push({
      label: () =>
        h(
          'div',
          { class: FONT_SIZE_CLASS, style: { color: themeVars.value.error } },
          t('canvasUI.components.themes.CThemeManagement.actions.delete'),
        ),
      key: 'delete',
      icon: () => h(CIcon, { icon: 'trash', color: themeVars.value.error }),
    })
  }

  return actionsMenu
}

function handleActions(key: string) {
  if (['viewNewTab', 'edit', 'copy', 'delete', 'toggleDefault'].includes(key)) emit(key as keyof typeof emit)
}
</script>
